/**
 * Box 5, the agency and organization representatives.
 *
 * The printed sheet has four rows and no more, so Add stops at four rather than
 * letting a fifth be typed and silently dropped from the export.
 */

import type React from 'react';
import { useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { ICS204_TABLE } from '../AssignmentList/assignmentCardStyles';
import { ICS203_AGENCY_COLUMNS } from '../ics203BoxLabels';

/** The number of agency rows the printed form has room for. */
export const ICS203_MAX_AGENCIES = 4;

type AgencyDraft = { agency_name: string; representative_name: string };

/** The columns box 5 prints, which is a subset of the stored agency record. */
export type Ics203Agency = {
  id?: string;
  agency_name?: string;
  representative_name?: string;
};

export type Ics203AgencyTableProps = {
  agencies: readonly Ics203Agency[];
  onCreate: () => void;
  onUpdate: (agency: Ics203Agency, patch: AgencyDraft) => void;
  onDelete: (id: string) => void;
  readOnly?: boolean;
};

const EMPTY_DRAFT: AgencyDraft = { agency_name: '', representative_name: '' };

export const Ics203AgencyTable: React.FC<Ics203AgencyTableProps> = ({
  agencies,
  onCreate,
  onUpdate,
  onDelete,
  readOnly = false,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AgencyDraft>(EMPTY_DRAFT);

  const startEdit = (agency: Ics203Agency) => {
    setEditingId(agency.id ?? null);
    setDraft({
      agency_name: agency.agency_name ?? '',
      representative_name: agency.representative_name ?? '',
    });
  };

  const rowLabel = (agency: Ics203Agency) =>
    agency.agency_name?.trim() || 'this agency';

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
      {readOnly ? null : (
        <div className="tw:flex tw:justify-end">
          <Button
            type="button"
            size="sm"
            disabled={agencies.length >= ICS203_MAX_AGENCIES}
            onClick={onCreate}
          >
            <Plus className="tw:size-4" />
            Add Agency
          </Button>
        </div>
      )}

      <div className="tw:overflow-x-auto">
        <Table className={ICS204_TABLE} data-testid="ics203-agencies">
          <TableHeader>
            <TableRow>
              {ICS203_AGENCY_COLUMNS.map((column) => (
                <TableHead key={column.label}>{column.label}</TableHead>
              ))}
              {readOnly ? null : <TableHead className="tw:w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {agencies.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={ICS203_AGENCY_COLUMNS.length + (readOnly ? 0 : 1)}
                  className="tw:text-center"
                >
                  No agencies added.
                </TableCell>
              </TableRow>
            ) : (
              agencies.map((agency) => {
                const isEditing = editingId != null && editingId === agency.id;
                const label = rowLabel(agency);

                return (
                  <TableRow key={agency.id}>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          className="tw:min-w-32"
                          aria-label={`Agency / Organization for ${label}`}
                          placeholder="Enter agency..."
                          value={draft.agency_name}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              agency_name: event.target.value,
                            })
                          }
                        />
                      ) : (
                        agency.agency_name || '—'
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          className="tw:min-w-32"
                          aria-label={`Name for ${label}`}
                          placeholder="Enter name..."
                          value={draft.representative_name}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              representative_name: event.target.value,
                            })
                          }
                        />
                      ) : (
                        agency.representative_name || '—'
                      )}
                    </TableCell>
                    {readOnly ? null : (
                      <TableCell>
                        <div className="tw:flex tw:justify-end tw:gap-1">
                          {isEditing ? (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Cancel editing ${label}`}
                                onClick={() => setEditingId(null)}
                              >
                                <X className="tw:size-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Save ${label}`}
                                onClick={() => {
                                  onUpdate(agency, draft);
                                  setEditingId(null);
                                }}
                              >
                                <Check className="tw:size-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Edit ${label}`}
                                onClick={() => startEdit(agency)}
                              >
                                <Pencil className="tw:size-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Delete ${label}`}
                                onClick={() => agency.id && onDelete(agency.id)}
                              >
                                <Trash2 className="tw:size-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <span className="tw:text-sm tw:text-muted-foreground">
        {agencies.length} Agenc{agencies.length === 1 ? 'y' : 'ies'}
      </span>
    </div>
  );
};

export default Ics203AgencyTable;
